import React, { useContext, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Carousel, StructuredListing } from '@hz-design-system/web-ui';
import { getURlWithoutJPG } from '@ecg-marktplaats/media-util-js-lib';

import { trackGAEvent } from '../../../utils/gaTracking';
import EnvironmentContext from '../../../contexts/EnvironmentContext';
import formatPrice from '../../FeedsApp/components/Feeds/utils/formatPrice';

import Classes from './ListingItemCarousel.scss';
import { TCarouselListingItem } from 'types/TCarousels';

type TListingItemCarouselProps = {
  type: string;
  title: string;
  items: TCarouselListingItem[];
  favoriteAdIdList: string[];
  onFavClickHandler: (item: TCarouselListingItem, isFavorite: boolean) => Promise<void>;
  onErrorHandler?: (erroredItem: TCarouselListingItem) => Promise<void>;
};

const ListingItemCarousel = ({
  type,
  title,
  items,
  favoriteAdIdList,
  onFavClickHandler,
  onErrorHandler = async () => {},
}: TListingItemCarouselProps) => {
  const { userLoggedIn } = useContext(EnvironmentContext);
  const { t } = useI18nContext();

  useEffect(() => {
    trackGAEvent('Displayed', `${type}_carousel`);
  }, [type]);

  const onClickHandler = (carouselType: string, index: number) => {
    trackGAEvent('Clicked', carouselType, index);
  };

  return (
    <Carousel title={title} id={type} headerClassName="PageContent-offset Carousel-title">
      <TransitionGroup component={null}>
        {items.map((item, index) => (
          <CSSTransition key={item.itemId} timeout={400} classNames="item">
            <StructuredListing
              linkProps={{ href: item.url, onClick: () => onClickHandler(type, index) }}
              className={Classes.item}
              title={item.title}
              price={item.formattedPrice ? String(item.price) : formatPrice(item.price, t, true, item.isPricePerMonth)}
              imageSrc={getURlWithoutJPG(item.imageSrc)}
              imageLoading={index <= 1 ? 'eager' : 'lazy'}
              decoding="async"
              onError={() => onErrorHandler(item)}
              isFavorite={favoriteAdIdList.includes(item.itemId)}
              showFav={Boolean(userLoggedIn)}
              onHeart={() => onFavClickHandler(item, favoriteAdIdList.includes(item.itemId))}
              badges={item.expired ? { errorText: t('cars.carousels.status.expired') } : undefined}
            />
          </CSSTransition>
        ))}
      </TransitionGroup>
    </Carousel>
  );
};

export default ListingItemCarousel;
